import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const VOSA_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/admin/integrations/vosa`;

/**
 * VosaBackfillWizard — steps admins through every LIVE property that still has
 * blank Vosa tags. Each step shows the property's missing fields; inputs are
 * saved into `property.cms_overrides` so subsequent feed generations pick
 * them up automatically.
 */
export default function VosaBackfillWizard({ open, onOpenChange, onSaved }) {
  const [rows, setRows] = useState([]);
  const [idx, setIdx] = useState(0);
  const [detail, setDetail] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${VOSA_BASE}/backfill`, { withCredentials: true });
        const incomplete = (r.data.properties || []).filter((p) => p.missing_count > 0);
        setRows(incomplete);
        setIdx(0);
      } catch (e) {
        toast.error("Failed to load properties");
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open || !rows[idx]) return;
    (async () => {
      setLoading(true);
      setValues({});
      try {
        const r = await axios.get(`${VOSA_BASE}/backfill/${rows[idx].property_id}`, {
          withCredentials: true,
        });
        setDetail(r.data);
      } catch (e) {
        toast.error("Failed to load property");
      } finally {
        setLoading(false);
      }
    })();
  }, [idx, rows, open]);

  const save = async ({ advance = false } = {}) => {
    if (!detail) return;
    // Values keyed by cms_field. Skip blanks — the admin explicitly left them.
    const payload = {};
    for (const f of detail.missing_fields) {
      const key = f.cms_field;
      if (!key) continue;
      const v = values[f.composite_key];
      if (v !== undefined && v !== "") payload[key] = v;
    }
    if (Object.keys(payload).length === 0) {
      if (advance) return next();
      toast.message("Nothing to save");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.put(
        `${VOSA_BASE}/backfill/${detail.property_id}`,
        { values: payload },
        { withCredentials: true },
      );
      toast.success(`Saved ${r.data.saved} field${r.data.saved === 1 ? "" : "s"}`);
      onSaved && onSaved();
      if (advance) next();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const next = () => setIdx((i) => Math.min(i + 1, rows.length - 1));
  const prev = () => setIdx((i) => Math.max(i - 1, 0));

  const total = rows.length;
  const current = rows[idx];
  const step = total ? idx + 1 : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0"
        data-testid="vosa-backfill-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <DialogTitle className="flex items-center justify-between">
            <span>Backfill Missing Vosa Fields</span>
            <span className="text-xs text-muted-foreground font-normal">
              {total ? `${step} of ${total}` : "0 properties"}
            </span>
          </DialogTitle>
          {total > 0 && <Progress value={(step / total) * 100} className="h-1 mt-2" />}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {loading && <div className="text-sm text-muted-foreground">Loading…</div>}

          {!loading && total === 0 && (
            <div className="py-12 text-center text-sm text-muted-foreground" data-testid="backfill-complete">
              Every LIVE property has 100% Vosa coverage. Nothing to backfill.
            </div>
          )}

          {!loading && current && detail && (
            <div className="space-y-4" data-testid="backfill-current-property">
              <div>
                <div className="text-lg font-medium">{detail.title}</div>
                <div className="text-xs text-muted-foreground">
                  {detail.display_address} · {detail.missing_fields.length} field
                  {detail.missing_fields.length === 1 ? "" : "s"} missing
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {detail.missing_fields.map((f) => (
                  <div key={f.composite_key} className="space-y-1">
                    <Label className="text-xs flex items-center gap-2">
                      <span className="font-mono">{f.vosa_field}</span>
                      <span className="text-[10px] uppercase text-muted-foreground">
                        {f.group}
                      </span>
                      {f.required && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      value={values[f.composite_key] || ""}
                      onChange={(e) =>
                        setValues((s) => ({ ...s, [f.composite_key]: e.target.value }))
                      }
                      placeholder={f.cms_field ? `${f.cms_field} (${f.type})` : `(${f.type})`}
                      className="rounded-none h-8 text-sm"
                      data-testid={`backfill-input-${f.vosa_field}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="border-t px-6 py-3 flex items-center justify-between gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={prev}
            disabled={idx === 0 || loading}
            className="rounded-none"
            data-testid="backfill-prev-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Prev
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="rounded-none"
              data-testid="backfill-close-btn"
            >
              <X className="h-4 w-4 mr-1" /> Close
            </Button>
            {current && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => save({ advance: false })}
                  disabled={saving || loading}
                  className="rounded-none"
                  data-testid="backfill-save-btn"
                >
                  <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save"}
                </Button>
                <Button
                  size="sm"
                  onClick={() => save({ advance: true })}
                  disabled={saving || loading || idx === total - 1}
                  className="rounded-none"
                  data-testid="backfill-save-next-btn"
                >
                  Save & Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
                {idx === total - 1 && (
                  <Button
                    size="sm"
                    onClick={() => save({ advance: false }).then(() => onOpenChange(false))}
                    disabled={saving || loading}
                    className="rounded-none"
                    data-testid="backfill-finish-btn"
                  >
                    Save & Finish
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
